import { Alert, Button, Card, Col, Divider, Empty, Flex, Form, Input, List, Popconfirm, Row, Select, Space, Tabs, Tag, Typography } from "antd";
import ReactQuill from "react-quill-new";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmailTemplate, Language } from "@types";
import { useEditEmailTemplate, useResetEmailTemplate } from "@hooks";

interface Props {
  emailTemplate: EmailTemplate;
  languages: Language[];
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

type TemplateVariable = {
  key: string;
  description: string;
};

const notificationTemplateNames = new Set([
  "ChallengeCreatedPushFallback",
  "ChallengeFinishedPushFallback",
  "ChallengeDirectPushFallback",
  "TournamentCreatedPushFallback",
  "TournamentRoundPushFallback",
  "TournamentCompletedPushFallback",
]);

const notificationTemplateVariables: Record<string, { subject: string[]; body: string[] }> = {
  ChallengeCreatedPushFallback: {
    subject: ["opponentName"],
    body: ["firstname", "opponentName", "categoryName", "challengeName", "detailsHtml", "ctaUrl", "supportEmail", "footerSignature"],
  },
  ChallengeFinishedPushFallback: {
    subject: ["challengeName", "opponentName"],
    body: ["firstname", "opponentName", "challengeName", "detailsHtml", "ctaUrl", "supportEmail", "footerSignature"],
  },
  ChallengeDirectPushFallback: {
    subject: ["opponentName", "challengeName"],
    body: ["firstname", "opponentName", "challengeName", "detailsHtml", "ctaUrl", "supportEmail", "footerSignature"],
  },
  TournamentCreatedPushFallback: {
    subject: ["tournamentTitle"],
    body: ["firstname", "tournamentTitle", "scheduledStart", "detailsHtml", "ctaUrl", "supportEmail", "footerSignature"],
  },
  TournamentRoundPushFallback: {
    subject: ["roundName"],
    body: ["firstname", "tournamentTitle", "roundName", "deadlineText", "detailsHtml", "ctaUrl", "supportEmail", "footerSignature"],
  },
  TournamentCompletedPushFallback: {
    subject: ["tournamentTitle"],
    body: ["firstname", "tournamentTitle", "winnerName", "detailsHtml", "ctaUrl", "supportEmail", "footerSignature"],
  },
};

const EmailIframePreview = ({ html }: { html: string }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const resizeIframe = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument?.body) return;
    const height = iframe.contentDocument.documentElement.scrollHeight;
    iframe.style.height = Math.max(height, 200) + "px";
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const handleLoad = () => resizeIframe();
    iframe.addEventListener("load", handleLoad);
    return () => iframe.removeEventListener("load", handleLoad);
  }, [resizeIframe]);

  useEffect(() => {
    const timeout = setTimeout(resizeIframe, 150);
    return () => clearTimeout(timeout);
  }, [html, resizeIframe]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={html}
      sandbox="allow-same-origin"
      style={{ width: "100%", border: "none", minHeight: 200 }}
      title="Email preview"
    />
  );
};

const getVariableDescription = (key: string, t: (value: string) => string) => t(`emailTemplateEditor.variableDescriptions.${key}`);

const getTemplateVariables = (templateName: string, t: (key: string) => string) => {
  const subjectVariables: TemplateVariable[] = [];
  const bodyVariables: TemplateVariable[] = [];

  if (notificationTemplateNames.has(templateName)) {
    const templateVariables = notificationTemplateVariables[templateName];
    subjectVariables.push(...templateVariables.subject.map((key) => ({ key: `{{var:${key}}}`, description: getVariableDescription(key, t) })));
    bodyVariables.push(...templateVariables.body.map((key) => ({ key: `{{var:${key}}}`, description: getVariableDescription(key, t) })));
  } else if (templateName === "ForgotPassword" || templateName === "PasswordForgot") {
    subjectVariables.push({ key: "{{var}}", description: t("emailTemplateEditor.variableDescriptions.subjectShortcutForgotPassword") });
    bodyVariables.push(
      { key: "{{var:firstname}}", description: t("emailTemplateEditor.variableDescriptions.firstname") },
      { key: "{{var:adminFirstName}}", description: t("emailTemplateEditor.variableDescriptions.adminFirstName") },
      { key: "{{var:adminLastName}}", description: t("emailTemplateEditor.variableDescriptions.adminLastName") },
      { key: "{{var:examLink}}", description: t("emailTemplateEditor.variableDescriptions.examLink") },
      { key: "{{var:password}}", description: t("emailTemplateEditor.variableDescriptions.password") },
      { key: "{{var:signature}}", description: t("emailTemplateEditor.variableDescriptions.signature") }
    );
  } else {
    subjectVariables.push({ key: "{{var}}", description: t("emailTemplateEditor.variableDescriptions.subjectShortcutExam") });
    bodyVariables.push(
      { key: "{{var:firstname}}", description: t("emailTemplateEditor.variableDescriptions.firstname") },
      { key: "{{var:adminFirstName}}", description: t("emailTemplateEditor.variableDescriptions.adminFirstName") },
      { key: "{{var:adminLastName}}", description: t("emailTemplateEditor.variableDescriptions.adminLastName") },
      { key: "{{var:dayOfWeek}}", description: t("emailTemplateEditor.variableDescriptions.dayOfWeek") },
      { key: "{{var:day}}", description: t("emailTemplateEditor.variableDescriptions.day") },
      { key: "{{var:month}}", description: t("emailTemplateEditor.variableDescriptions.month") },
      { key: "{{var:year}}", description: t("emailTemplateEditor.variableDescriptions.year") },
      { key: "{{var:time}}", description: t("emailTemplateEditor.variableDescriptions.time") },
      { key: "{{var:examTitle}}", description: t("emailTemplateEditor.variableDescriptions.examTitle") },
      { key: "{{var:examLink}}", description: t("emailTemplateEditor.variableDescriptions.examLink") },
      { key: "{{var:token}}", description: t("emailTemplateEditor.variableDescriptions.token") },
      { key: "{{var:zoomMeetingId}}", description: t("emailTemplateEditor.variableDescriptions.zoomMeetingId") },
      { key: "{{var:zoomPasscode}}", description: t("emailTemplateEditor.variableDescriptions.zoomPasscode") },
      { key: "{{var:signature}}", description: t("emailTemplateEditor.variableDescriptions.signature") }
    );
  }

  return { subjectVariables, bodyVariables };
};

export const EmailTemplateForm = ({ emailTemplate, languages, setIsModalOpen }: Props) => {
  const [emailTemplateForm] = Form.useForm<EmailTemplate>();
  const [currentTemplate, setCurrentTemplate] = useState(emailTemplate);
  const { t } = useTranslation();
  const editEmailTemplate = useEditEmailTemplate();
  const resetEmailTemplate = useResetEmailTemplate();
  const bodyValue = Form.useWatch("body", emailTemplateForm) ?? currentTemplate.body;
  const previewSubject = Form.useWatch("subject", emailTemplateForm) ?? currentTemplate.subject;
  const languageName = languages.find((language) => language.id === currentTemplate.languageId)?.name ?? currentTemplate.languageId;
  const { subjectVariables, bodyVariables } = getTemplateVariables(currentTemplate.name, t);

  useEffect(() => {
    setCurrentTemplate(emailTemplate);
    emailTemplateForm.setFieldsValue(emailTemplate);
  }, [emailTemplate, emailTemplateForm]);

  const onSubmit = (values: EmailTemplate) => {
    editEmailTemplate.mutate({ ...currentTemplate, ...values }, {
      onSuccess() {
        setIsModalOpen(false);
      }
    });
  };

  const onReset = () => {
    resetEmailTemplate.mutate(currentTemplate, {
      onSuccess(resetTemplate) {
        setCurrentTemplate(resetTemplate);
        emailTemplateForm.setFieldsValue(resetTemplate);
      }
    });
  };

  return (
    <Form layout="vertical" form={emailTemplateForm} onFinish={onSubmit}>
      <Form.Item name={["id"]} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={["name"]} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={["languageId"]} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={["tenantId"]} hidden>
        <Input />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item label={t("emailTemplateEditor.templateKey")}>
            <Input value={emailTemplate.name} disabled />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t("language")}>
            <Input value={languageName} disabled />
          </Form.Item>
        </Col>
      </Row>

      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary">{t("emailTemplateEditor.description")}</Typography.Text>
        <Tag color={currentTemplate.tenantId ? "gold" : "blue"}>
          {currentTemplate.tenantId ? t("emailTemplateEditor.source.tenant") : t("emailTemplateEditor.source.global")}
        </Tag>
      </Flex>

      <Form.Item
        name={["state"]}
        label={t("emailTemplateEditor.status")}
        rules={[{ required: true, message: t("emailTemplateEditor.status") }]}
      >
        <Select
          options={[
            { label: t("emailTemplateEditor.states.active"), value: 1 },
            { label: t("emailTemplateEditor.states.inactive"), value: 2 },
          ]}
        />
      </Form.Item>

      <Form.Item
        name={["subject"]}
        label={t("emailTemplateEditor.subject")}
        rules={[{ required: true, message: t("emailTemplateEditor.subject") }]}
      >
        <Input placeholder={t("emailTemplateEditor.subject")} />
      </Form.Item>

      <Row gutter={20} align="top">
        <Col xs={24}>
          <Card
            style={{ marginBottom: 16 }}
            styles={{ body: { padding: 0 } }}
          >
            <Tabs
              defaultActiveKey="source"
              style={{ padding: "0 16px" }}
              items={[
                {
                  key: "visual",
                  label: t("emailTemplateEditor.visualEditor"),
                  children: (
                    <div style={{ padding: "0 0 16px 0" }}>
                      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                        {t("emailTemplateEditor.visualEditorHint")}
                      </Typography.Text>
                      <Form.Item
                        name={["body"]}
                        rules={[{ required: true, message: t("emailTemplateEditor.body") }]}
                        style={{ marginBottom: 0 }}
                      >
                        <ReactQuill theme="snow" style={{ minHeight: 420, marginBottom: 42 }} />
                      </Form.Item>
                    </div>
                  ),
                },
                {
                  key: "source",
                  label: t("emailTemplateEditor.htmlSource"),
                  children: (
                    <div style={{ padding: "0 0 16px 0" }}>
                      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                        {t("emailTemplateEditor.sourceEditorHint")}
                      </Typography.Text>
                      <Form.Item
                        name={["body"]}
                        rules={[{ required: true, message: t("emailTemplateEditor.body") }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input.TextArea
                          rows={22}
                          style={{ fontFamily: "monospace", fontSize: 13, lineHeight: "1.5" }}
                          spellCheck={false}
                        />
                      </Form.Item>
                    </div>
                  ),
                },
              ]}
            />
          </Card>

          <Card title={t("emailTemplateEditor.preview")} style={{ marginBottom: 16 }}>
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <div>
                <Typography.Text type="secondary">{t("emailTemplateEditor.subject")}</Typography.Text>
                <Typography.Title level={4} style={{ marginTop: 8, marginBottom: 0 }}>
                  {previewSubject || t("emailTemplateEditor.emptyPreview")}
                </Typography.Title>
              </div>
              <Divider style={{ margin: 0 }} />
              <div
                style={{
                  minHeight: 200,
                  borderRadius: 8,
                  border: "1px solid var(--ant-color-border)",
                  background: "#f5f5f5",
                  overflow: "hidden",
                }}
              >
                {bodyValue ? (
                  <EmailIframePreview html={bodyValue} />
                ) : (
                  <div style={{ padding: 24 }}>
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("emailTemplateEditor.emptyPreview")} />
                  </div>
                )}
              </div>
            </Space>
          </Card>

          <Card title={t("emailTemplateEditor.availableVariables")}>
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Alert
                type="info"
                showIcon
                message={t("emailTemplateEditor.variableHelp")}
                description={t("emailTemplateEditor.variableHelpDescription")}
              />

              {subjectVariables.length > 0 && (
                <div>
                  <Typography.Title level={5}>{t("emailTemplateEditor.subjectVariables")}</Typography.Title>
                  <List
                    size="small"
                    bordered
                    dataSource={subjectVariables}
                    renderItem={(item) => (
                      <List.Item>
                        <Flex vertical style={{ width: "100%" }}>
                          <Typography.Text code>{item.key}</Typography.Text>
                          <Typography.Text type="secondary">{item.description}</Typography.Text>
                        </Flex>
                      </List.Item>
                    )}
                  />
                </div>
              )}

              {bodyVariables.length > 0 && (
                <div>
                  <Typography.Title level={5}>{t("emailTemplateEditor.bodyVariables")}</Typography.Title>
                  <List
                    size="small"
                    bordered
                    dataSource={bodyVariables}
                    renderItem={(item) => (
                      <List.Item>
                        <Flex vertical style={{ width: "100%" }}>
                          <Typography.Text code>{item.key}</Typography.Text>
                          <Typography.Text type="secondary">{item.description}</Typography.Text>
                        </Flex>
                      </List.Item>
                    )}
                  />
                </div>
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      <Flex justify="space-between">
        <Form.Item style={{ marginBottom: 0 }}>
          <Popconfirm
            title={t("emailTemplateEditor.resetConfirmTitle")}
            description={t("emailTemplateEditor.resetConfirmDescription")}
            onConfirm={onReset}
            okText={t("reset")}
            cancelText={t("cancel")}
          >
            <Button loading={resetEmailTemplate.isPending}>
              {t("reset")}
            </Button>
          </Popconfirm>
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={editEmailTemplate.isPending}
          >
            {t("forms.category.save")}
          </Button>
        </Form.Item>
      </Flex>
    </Form>
  );
};